import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Seo } from '../../components/Seo';

interface ServerErrorPageProps {
  /**
   * The error caught by the ErrorBoundary. Optional — when this page is
   * rendered directly by the server for a true 5xx, no React error is
   * available. Application Insights logging includes the error's message
   * + stack when present.
   */
  readonly error?: Error;
  /**
   * Reset callback exposed by the ErrorBoundary. When present, the "Try
   * again" button calls this to attempt re-rendering the failed branch.
   * Fallback when absent: reload the page.
   */
  readonly resetError?: () => void;
}

/**
 * 500 — Server error. Per spec `404-500-error-pages-2026-05-16.md`.
 *
 * Same visual treatment as 404. Three action cards: Try again / Home /
 * Email us. Secondary line points support@conscierra.au for existing-
 * service-issue urgency.
 *
 * Application Insights logging: emits an exception event with the error
 * details when invoked from the ErrorBoundary path.
 */
export default function ServerErrorPage({ error, resetError }: ServerErrorPageProps = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const appInsights = (
      window as unknown as {
        appInsights?: { trackException?: (e: { exception: Error; properties?: Record<string, unknown> }) => void };
      }
    ).appInsights;
    if (error && appInsights?.trackException) {
      appInsights.trackException({
        exception: error,
        properties: { pathname: window.location.pathname, source: 'ErrorBoundary' },
      });
    }
  }, [error]);

  function handleTryAgain() {
    if (resetError) {
      resetError();
    } else if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  return (
    <section className="bg-white py-20">
      <Seo
        title="500 — server error — Conscierra"
        description="Something went wrong on our end. We’ve logged the error and our team will look at it."
        canonicalPath="/500"
        noindex
      />
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-red-600">
          500
        </p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-fern-600 sm:text-4xl">
          Something went wrong on our end.
        </h1>
        <p className="mt-4 text-base leading-7 text-gray-700">
          We&rsquo;ve logged the error and our team will look at it. In the meantime,
          here are your options.
        </p>

        <ul className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3">
          <li>
            <button
              type="button"
              onClick={handleTryAgain}
              className="block w-full rounded-xl border border-sand-200 bg-sand-50 px-5 py-4 text-sm font-semibold text-fern-700 hover:bg-fern-50 hover:border-fern-200"
            >
              Try again
            </button>
          </li>
          <li>
            <Link
              to="/"
              className="block rounded-xl border border-sand-200 bg-sand-50 px-5 py-4 text-sm font-semibold text-fern-700 hover:bg-fern-50 hover:border-fern-200"
            >
              Go to the home page
            </Link>
          </li>
          <li>
            <a
              href="mailto:info@conscierra.au"
              className="block rounded-xl border border-sand-200 bg-sand-50 px-5 py-4 text-sm font-semibold text-fern-700 hover:bg-fern-50 hover:border-fern-200"
            >
              Email us directly
            </a>
          </li>
        </ul>

        <p className="mt-10 text-sm leading-6 text-gray-600">
          If this is urgent (an existing service issue), email{' '}
          <a href="mailto:support@conscierra.au" className="text-fern-700 hover:underline">
            support@conscierra.au
          </a>
          {' '}&mdash; that goes straight to the service desk.
        </p>
      </div>
    </section>
  );
}
